<script setup lang="ts">
  // Native printer setup wizard (Capacitor Android). SAVE-AT-FINISH: nothing
  // persists before commitNativeWizard runs on Finish, so cancelling at any
  // step needs ZERO cleanup — do NOT port the desktop wizard's
  // cleanupAddedDevice (that one creates bridge entries mid-flow).
  import { computed, getCurrentInstance, nextTick, ref } from 'vue';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { useNativePrintingUi } from '@/composition/printing/use-native-printing-ui';
  import {
    INativeWizardDevice,
    commitNativeWizard,
  } from '@/composition/printing/native-wizard-commit';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import V2WizardSteps from '../../wizard/V2WizardSteps.vue';
  import NativeStepConnection, {
    TNativeWizardConnection,
  } from './NativeStepConnection.vue';
  import NativeStepDevice from './NativeStepDevice.vue';
  import NativeStepType, { TNativeWizardType } from './NativeStepType.vue';
  import NativeStepTest from './NativeStepTest.vue';
  import NativeStepAssign, {
    INativeWizardAssignResult,
  } from './NativeStepAssign.vue';

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const ui = useUi();
  const nativeUi = useNativePrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  // #region wizard state (all local until Finish)

  const STEPS = ['connection', 'device', 'type', 'test', 'assign'] as const;
  type TStep = typeof STEPS[number];

  const stepIndex = ref(0);
  const currentStep = computed<TStep>(() => STEPS[stepIndex.value]);

  const stepLabels = computed(() => [
    i18nT('printingV2.stepConnection'),
    i18nT('printingV2.stepDevice'),
    i18nT('printingV2.stepType'),
    i18nT('printingV2.stepTest'),
    i18nT('printingV2.stepAssign'),
  ]);

  const connection = ref<TNativeWizardConnection | null>(null);
  const device = ref<INativeWizardDevice | null>(null);
  const wizardType = ref<TNativeWizardType | null>(null);
  /** ESC/POS test codepage, seeded from the ticket destination's setting. */
  const encoding = ref(nativeUi.printing.ticketPrintingSettings.value.encoding);
  /** Only a symbols-flow pick writes encodings back on Finish. */
  const encodingPicked = ref(false);
  const finishing = ref(false);
  const commitError = ref<string | null>(null);

  // #endregion

  // #region step transitions

  function onConnectionSelect(value: TNativeWizardConnection) {
    connection.value = value;
    stepIndex.value = 1;
  }

  function onDeviceSelect(value: INativeWizardDevice) {
    device.value = value;
    stepIndex.value = 2;
  }

  function onTypeSelect(value: TNativeWizardType) {
    wizardType.value = value;
    stepIndex.value = 3;
  }

  function onTestConfirmed() {
    stepIndex.value = 4;
  }

  function onEncodingChange(value: string) {
    encoding.value = value;
    encodingPicked.value = true;
  }

  /** Test step's "it's a label printer" hint → back to the Type step. */
  function onChangeType() {
    stepIndex.value = 2;
  }

  function back() {
    if (stepIndex.value === 0 || finishing.value) return;
    commitError.value = null;
    stepIndex.value -= 1;
  }

  // #endregion

  // #region finish (the ONLY write — see native-wizard-commit.ts)

  async function onFinish(result: INativeWizardAssignResult) {
    const currentDevice = device.value;
    const currentType = wizardType.value;
    if (!currentDevice || !currentType || finishing.value) return;

    finishing.value = true;
    commitError.value = null;
    // Renderer-safety: don't mutate local reactive state after emit('close')
    // starts the modal teardown — only reset the spinner when staying open.
    let closed = false;
    try {
      commitNativeWizard(nativeUi.store, {
        device: currentDevice,
        name: result.name,
        printerType: currentType,
        selection: result.selection,
        encoding: encodingPicked.value ? encoding.value : null,
      });

      // Seed the new network printer's probe chip from the Device-step
      // probe so the card doesn't sit stateless until the next modal open.
      if (currentDevice.isNew) {
        nativeUi.probeStateByName.value = {
          ...nativeUi.probeStateByName.value,
          [result.name.trim()]: currentDevice.reachable ? 'online' : 'offline',
        };
      }

      ui.showToast(AlertType.Success, i18nT('printingV2.wizardDone'));
      // Let the commit's re-renders flush BEFORE starting the modal's leave
      // transition: sharing a flush with this write burst can trip Vue's
      // circular-update breaker, which aborts the flush and strands the
      // modal half-closed (vue-js-modal auto-height render feedback).
      await nextTick();
      closed = true;
      emit('close');
    } catch (e) {
      // Late collision (e.g. a printer saved elsewhere mid-wizard): inline
      // on the Assign step, zero partial state was written.
      commitError.value =
        e instanceof Error ? e.message : i18nT('printingV2.actionFailed');
    } finally {
      if (!closed) finishing.value = false;
    }
  }

  // #endregion
</script>

<template>
  <div class="v2-native-printer-setup-wizard">
    <div class="wizard-header">
      <h3 class="wizard-title">{{ $t('printingV2.wizardTitle') }}</h3>
      <button class="wizard-close" type="button" @click="emit('close')">
        <PosUiIcon name="close" :size="20" />
      </button>
    </div>

    <div class="wizard-steps-bar">
      <V2WizardSteps :steps="stepLabels" :current="stepIndex" />
    </div>

    <div class="wizard-body">
      <NativeStepConnection
        v-if="currentStep === 'connection'"
        @select="onConnectionSelect"
      />
      <NativeStepDevice
        v-else-if="currentStep === 'device' && connection"
        :connection="connection"
        @select="onDeviceSelect"
      />
      <NativeStepType v-else-if="currentStep === 'type'" @select="onTypeSelect" />
      <NativeStepTest
        v-else-if="currentStep === 'test' && device && wizardType"
        :device="device"
        :type="wizardType"
        :encoding="encoding"
        @confirmed="onTestConfirmed"
        @encoding-change="onEncodingChange"
        @change-type="onChangeType"
      />
      <NativeStepAssign
        v-else-if="currentStep === 'assign' && device && wizardType"
        :device="device"
        :type="wizardType"
        :finishing="finishing"
        :error="commitError"
        @finish="onFinish"
      />
    </div>

    <div class="wizard-footer">
      <PosUiButton
        v-if="stepIndex > 0"
        color="neutral"
        variant="ghost"
        icon="arrow-left"
        :disabled="finishing"
        @click="back"
      >
        {{ $t('printingV2.back') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-native-printer-setup-wizard {
    @apply tw-flex tw-flex-col tw-bg-white;

    .wizard-header {
      @apply tw-flex tw-items-center tw-justify-between tw-border-b tw-border-neutral-100 tw-px-5 tw-py-4;

      .wizard-title {
        @apply tw-m-0 tw-text-base tw-font-bold tw-text-neutral-900;
      }

      .wizard-close {
        @apply tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
      }
    }

    .wizard-steps-bar {
      @apply tw-px-5 tw-pt-4;
    }

    .wizard-body {
      @apply tw-min-h-[280px] tw-px-5 tw-py-4;
    }

    .wizard-footer {
      @apply tw-flex tw-min-h-[56px] tw-items-center tw-border-t tw-border-neutral-100 tw-px-5 tw-py-2;
    }
  }
</style>
